import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { ExpandableLink } from './sidebar-expandable';

export type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
  collectorOnly?: boolean;
  hideForCollector?: boolean;
  hideForApproacher?: boolean;
  disabled?: boolean;
  children?: NavItem[];
};

const BASE =
  'flex h-9 items-center gap-md rounded-lg px-md text-[13px] font-medium transition-colors';
const FADE = 'shrink-0 whitespace-nowrap transition-opacity duration-150';

function linkClass(isActive: boolean): string {
  return cn(
    BASE,
    isActive
      ? 'bg-brand-red/10 text-brand-red'
      : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary'
  );
}

function isHidden(item: NavItem, isAdmin: boolean, isColl: boolean): boolean {
  if (item.adminOnly && !isAdmin) return true;
  if (item.collectorOnly && !isColl) return true;
  if (item.hideForCollector && isColl) return true;
  if (item.hideForApproacher && !isAdmin && !isColl) return true;
  return false;
}

function DisabledLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}): ReactNode {
  return (
    <li>
      <span className={cn(BASE, 'text-text-tertiary/40')} title={item.label}>
        <span className="shrink-0">{item.icon}</span>
        <span className={cn(FADE, collapsed ? 'opacity-0' : 'opacity-100')}>
          {item.label}
        </span>
      </span>
    </li>
  );
}

function ActiveLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}): ReactNode {
  return (
    <li>
      <NavLink
        to={item.to}
        title={item.label}
        className={({ isActive }) => linkClass(isActive)}
      >
        <span className="shrink-0">{item.icon}</span>
        <span className={cn(FADE, collapsed ? 'opacity-0' : 'opacity-100')}>
          {item.label}
        </span>
      </NavLink>
    </li>
  );
}

export function SidebarLink(p: {
  item: NavItem;
  isAdmin: boolean;
  isCollector: boolean;
  collapsed: boolean;
}): ReactNode {
  if (isHidden(p.item, p.isAdmin, p.isCollector)) return null;
  if (p.item.disabled)
    return <DisabledLink item={p.item} collapsed={p.collapsed} />;
  if (p.item.children?.length)
    return <ExpandableLink item={p.item} collapsed={p.collapsed} />;
  return <ActiveLink item={p.item} collapsed={p.collapsed} />;
}
